import { useMemo } from "react";

import * as Sentry from "@sentry/react";
import { useIntl } from "react-intl";
import { Outlet, useParams } from "react-router-dom";

import { AtlassianAdminIcon } from "@atlaskit/logo";
import Lozenge from "@atlaskit/lozenge-next";
import { Main, Root } from "@atlaskit/navigation-system";
import {
    SideNav,
    SideNavHeader
} from "@atlaskit/navigation-system/layout/side-nav";
import { SideNavContent } from "@atlaskit/navigation-system/layout/side-nav";
import { SideNavToggleButton } from "@atlaskit/navigation-system/layout/side-nav";
import {
    TopNav,
    TopNavStart,
    TopNavEnd
} from "@atlaskit/navigation-system/layout/top-nav";
import { LinkMenuItem } from "@atlaskit/navigation-system/side-nav-items/link-menu-item";
import { MenuList } from "@atlaskit/navigation-system/side-nav-items/menu-list";
import { AppLogo } from "@atlaskit/navigation-system/top-nav-items";
import { Box, Inline, xcss } from "@atlaskit/primitives-next";

import { AIChatWidget } from "@/components/fastshift/src/common/ai-chat-widget";
import { PagePanelContextProvider } from "@/components/fastshift/src/common/contexts";
import { FeedbackCollector } from "@/components/fastshift/src/common/feedback-collector";
import { useRoutes } from "@/components/fastshift/src/common/hooks";
import { NavigationProfile } from "@/components/fastshift/src/common/navigation-profile";

import { useShouldShowConsentLandingPage } from "../../../pages/fastshift/src/hooks";
import { GenericErrorDisplay } from "../../displays";
import { beforeCaptureWithScope } from "../../utils/sentry-with-scope";

import { AccessControlledPageContent } from "./access-controlled-page-content";
import { DomainScopedNavHeader } from "./domain-scoped-nav-header";
import { useLoadDomainScopedState } from "./hooks";
import { usePageNavLinks } from "./hooks";
import { usePageLayoutAnalytics } from "./hooks";
import {
    usePortalAuthenticationAndAccessControl,
    UserAccessValidationState
} from "./hooks/use-portal-authentication-and-access-control";
import { messages } from "./messages";
import { PagePanel } from "./page-panel";

const mainStyles = xcss({
    padding: "space.200"
});

// Adapter to convert BaseLogoProps to LogoProps for AppLogo
// resolves incompatible typing issues from package versioning
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const AtlassianAdminIconAdapter = (props: any) => (
    <AtlassianAdminIcon {...props} />
);

export const PageLayout = () => {
    const { domainId } = useParams<{ domainId: string }>();
    const { paths } = useRoutes();

    const accessControlState = usePortalAuthenticationAndAccessControl();
    const { userAccessState, shouldShowSideNav, isPartnerOrInternal } =
        accessControlState;

    const shouldDisplayAIChatWidget =
        !accessControlState.isOnHomeRoute &&
        accessControlState.userAccessState ===
            UserAccessValidationState.VALIDATION_SUCCESS;

    const isViewingConsentLandingPage = useShouldShowConsentLandingPage();

    const shouldDisplayFeedbackCollector = useMemo(() => {
        return (
            !isViewingConsentLandingPage &&
            accessControlState.userAccessState ===
                UserAccessValidationState.VALIDATION_SUCCESS
        );
    }, [accessControlState, isViewingConsentLandingPage]);

    useLoadDomainScopedState(domainId || "");

    const {
        pageKey,
        links: pageMenuItems,
        isOnDomainScopedPage
    } = usePageNavLinks();

    const { sendSideNavLinkClickEvent } = usePageLayoutAnalytics();

    const { formatMessage } = useIntl();

    return (
        <Root>
            <PagePanelContextProvider>
                <TopNav>
                    <Sentry.ErrorBoundary
                        beforeCapture={beforeCaptureWithScope("top-nav")}
                        fallback={<GenericErrorDisplay />}
                    >
                        <TopNavStart>
                            {shouldShowSideNav && (
                                <SideNavToggleButton
                                    defaultCollapsed
                                    collapseLabel={formatMessage(
                                        messages.collapseSidebarLabel
                                    )}
                                    expandLabel={formatMessage(
                                        messages.expandSidebarLabel
                                    )}
                                />
                            )}
                            <AppLogo
                                icon={AtlassianAdminIconAdapter}
                                name={formatMessage(messages.fastshiftAppName)}
                                label={formatMessage(messages.homePageLabel)}
                                href={paths.home}
                            />
                            <Lozenge appearance="removed">
                                {formatMessage(messages.betaLozenge)}
                            </Lozenge>
                        </TopNavStart>

                        <TopNavEnd>
                            {shouldDisplayFeedbackCollector && (
                                <FeedbackCollector />
                            )}
                            <NavigationProfile />
                        </TopNavEnd>
                    </Sentry.ErrorBoundary>
                </TopNav>

                {shouldShowSideNav && (
                    <SideNav>
                        <Sentry.ErrorBoundary
                            beforeCapture={beforeCaptureWithScope("side-nav")}
                            fallback={<GenericErrorDisplay />}
                        >
                            {isPartnerOrInternal && isOnDomainScopedPage && (
                                <SideNavHeader>
                                    <DomainScopedNavHeader
                                        isOnDomainScopedPage={
                                            isOnDomainScopedPage
                                        }
                                    />
                                </SideNavHeader>
                            )}
                            {/* Nav Links */}
                            <SideNavContent>
                                <MenuList>
                                    {pageMenuItems.map((navItem) => {
                                        return (
                                            <LinkMenuItem
                                                key={navItem.key}
                                                href={navItem.link || ""}
                                                elemBefore={navItem.icon}
                                                isSelected={
                                                    navItem.key == pageKey
                                                }
                                                onClick={() => {
                                                    sendSideNavLinkClickEvent(
                                                        navItem.label,
                                                        navItem.link || "",
                                                        isOnDomainScopedPage
                                                    );
                                                }}
                                            >
                                                {navItem.label}
                                            </LinkMenuItem>
                                        );
                                    })}
                                </MenuList>
                            </SideNavContent>
                        </Sentry.ErrorBoundary>
                    </SideNav>
                )}

                <Main testId="main" id="main">
                    <Inline alignInline="center">
                        {shouldDisplayAIChatWidget && <AIChatWidget />}
                        <AccessControlledPageContent
                            userAccessState={userAccessState}
                        >
                            <Box xcss={mainStyles}>
                                <Sentry.ErrorBoundary
                                    beforeCapture={beforeCaptureWithScope(
                                        "page-content"
                                    )}
                                    fallback={<GenericErrorDisplay />}
                                >
                                    <Outlet />
                                </Sentry.ErrorBoundary>
                            </Box>
                        </AccessControlledPageContent>
                    </Inline>
                </Main>

                <PagePanel />
            </PagePanelContextProvider>
        </Root>
    );
};
